import React, { useContext, useEffect, useState } from "react";
import styles from "./styles/ApprovalModal.module.css";
import { LanguageContext } from "../../../Context/changeLanguageProvider";

/*
  The interstitial a customer sees before reviewing something their partner
  created — measured off approval_modal.svg.

  It exists to make one thing unmissable: this record is not live. The partner
  wrote it, nothing about it counts yet, and the customer decides whether it
  ever does. "Start Reviewing" hands off to the ordinary edit modal, which
  offers Approve and Delete where it would normally offer Save.
*/

const FALLBACK = {
  partnerApprovalText1: "{name} Created this Beneficiary",
  partnerApprovalText2: "{name} Created this Asset",
  partnerApprovalText3:
    "Before this beneficiary is added to your inheritance tree, you will need to approve them first. You can edit any field and manage the beneficiary's information.",
  partnerApprovalText4:
    "Please pay special attention to the beneficiary's contact details, as these will be used to notify them and allow them to log in to their beneficiary portal when the time comes.",
  partnerApprovalText5:
    "Only after you approve the beneficiary will they be added to your inheritance tree, and the attention button will be removed.",
  partnerApprovalText6:
    "If you do not want this beneficiary to appear in your inheritance tree, you can delete them instead of approving them.",
  partnerApprovalText7:
    "Before this asset is added to your account, you will need to approve it first. You can edit any field and manage the asset's information.",
  partnerApprovalText8:
    "Only after you approve the asset will it count towards your estate, and the attention marker will be removed.",
  partnerApprovalText9:
    "If you do not want this asset in your account, you can delete it instead of approving it.",
  partnerApprovalText10: "Cancel",
  partnerApprovalText11: "Start Reviewing",
  partnerApprovalText15: "Your partner",
  partnerApprovalText17: "{name} Created this Trusted Advisor",
  partnerApprovalText18:
    "Before this trusted advisor is added to your account, you will need to approve them first. You can edit any field and manage the advisor's information.",
  partnerApprovalText19:
    "Only after you approve the trusted advisor will they be attached to your assets and beneficiaries, and the attention marker will be removed.",
  partnerApprovalText20:
    "If you do not want this trusted advisor in your account, you can delete them instead of approving them.",
};

/*
  Which strings each subject uses. A beneficiary gets the contact-details
  paragraph; an asset has no equivalent, so it has one paragraph fewer rather
  than a reworded version of one that does not apply.
*/
const SUBJECTS = {
  beneficiary: {
    titleKey: "partnerApprovalText1",
    bodyKeys: [
      "partnerApprovalText3",
      "partnerApprovalText4",
      "partnerApprovalText5",
      "partnerApprovalText6",
    ],
  },
  asset: {
    titleKey: "partnerApprovalText2",
    bodyKeys: [
      "partnerApprovalText7",
      "partnerApprovalText8",
      "partnerApprovalText9",
    ],
  },
  trustee: {
    titleKey: "partnerApprovalText17",
    bodyKeys: [
      "partnerApprovalText18",
      "partnerApprovalText19",
      "partnerApprovalText20",
    ],
  },
};

export default function ApprovalModal({
  subject = "beneficiary",
  partnerName,
  onStart,
  onClose,
}) {
  const websiteCurrentLang = useContext(LanguageContext).language;
  const [content, setContent] = useState(
    websiteCurrentLang?.content?.User?.PartnerApproval?.PartnerApproval ||
      FALLBACK
  );
  useEffect(() => {
    setContent(
      websiteCurrentLang?.content?.User?.PartnerApproval?.PartnerApproval ||
        FALLBACK
    );
  }, [websiteCurrentLang]);

  // The page behind must not scroll while this is open.
  useEffect(() => {
    document.documentElement.style.overflow = "hidden";
    return () => {
      document.documentElement.style.overflow = "unset";
    };
  }, []);

  const t = (key) => (content && content[key]) || FALLBACK[key];

  const keys = SUBJECTS[subject] || SUBJECTS.beneficiary;
  const name = String(partnerName || "").trim() || t("partnerApprovalText15");

  /*
    The title is one string with the name inside it, so translations can put the
    name where their grammar wants it -- French leads with it too, but that is
    the translator's call, not the layout's. Split on the placeholder so only
    the name takes the lighter blue the design gives it.
  */
  const title = t(keys.titleKey);
  const [before, after] = title.split("{name}");

  return (
    <div className={styles.background} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          className={styles.close}
          onClick={onClose}
          aria-label={t("partnerApprovalText10")}
        >
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M5 5l14 14M19 5L5 19" />
          </svg>
        </button>

        <div className={styles.body}>
          <div className={styles.icon} aria-hidden="true">
            <svg viewBox="0 0 56 56">
              <circle
                cx="28"
                cy="28"
                r="24"
                fill="none"
                stroke="#0091EA"
                strokeWidth="4"
                strokeLinecap="round"
                strokeDasharray="132 24"
                transform="rotate(-58 28 28)"
              />
              <path
                d="M18 28.5l7 7 13-14"
                fill="none"
                stroke="#0091EA"
                strokeWidth="4"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>

          <h2 className={styles.title}>
            <span className={styles.title_name}>{before}</span>
            {name}
            <span className={styles.title_name}>{after}</span>
          </h2>
          <div className={styles.rule} />

          {keys.bodyKeys.map((key) => (
            <p className={styles.paragraph} key={key}>
              {t(key)}
            </p>
          ))}
        </div>

        {/*
          The footer's wave is part of the shape, so it is drawn rather than
          layered behind: an image would need a second file at every width.
          The path has to close on the viewBox's bottom edge: stopping short of
          it leaves a hairline of the card's white between the wave and the
          footer's own background, which the -1px overlap is too small to cover.
        */}
        <div className={styles.footer}>
          <svg
            className={styles.footer_wave}
            viewBox="0 0 436 22"
            preserveAspectRatio="none"
            aria-hidden="true"
          >
            <path
              d="M0 12C80 6 150 2 230 4c80 2 150 8 206 5V22H0z"
              fill="#26323E"
            />
          </svg>
          <div className={styles.footer_actions}>
            <button type="button" className={styles.cancel} onClick={onClose}>
              {t("partnerApprovalText10")}
            </button>
            <button type="button" className={styles.start} onClick={onStart}>
              {t("partnerApprovalText11")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
